/**
 * ConfirmTypedDialog — a confirmation that requires typing a word before the
 * action unlocks.
 *
 * For operations where a single click is too cheap: moving residents out (which
 * deactivates their devices and monitoring) or deleting them outright. The typed
 * word buys a moment of deliberation that an OK button does not.
 *
 * Exists as one component because the gate itself is the safety control, and
 * three hand-rolled copies of it would eventually disagree — one would forget to
 * block Enter, or to re-lock while saving. Callers supply the wording; the gate
 * is not configurable beyond the word.
 *
 * Mount it conditionally (`{open ? <ConfirmTypedDialog … /> : null}`). Every
 * caller does, which is what resets the typed value between uses — there is no
 * internal reset to forget.
 */

import { useState, useId } from 'react';
/* Its own stylesheets (audit C12). index.jsx already loads these first, so this does not
   move them in the cascade; it states the dependency, so the component stays styled
   wherever it is reused. */
import '../../styles/dp-primitives.css';
import '../../styles/pp-primitives.css';

/** Matched case-insensitively and trimmed: the point is deliberation, not spelling. */
export const CONFIRM_WORD = 'confirm';

const ConfirmTypedDialog = ({
  title,
  children,                 // the explanation — what is about to happen
  confirmLabel,
  savingLabel,
  onClose,
  onConfirm,
  saving = false,
  /* Optional help-guide anchors. Only the single-resident move-out sets these;
     everything else leaves them undefined so no stray duplicate ids appear
     (one id must match one visible element — see V2_DESIGN_INSTRUCTIONS §15). */
  helpIdInput,
  helpIdConfirm,
  /* Set when the dialog can open on top of a drawer. `.pp-dialog-backdrop` sits
     at --z-modal-bg (400) but drawers sit at --z-modal (500), so without this the
     dialog renders behind the drawer that launched it. */
  elevated = false,
}) => {
  const [typed, setTyped] = useState('');
  /* Unique per instance so two dialogs could coexist without colliding ids. */
  const reactId = useId();
  const inputId = `confirm-typed-${reactId}`;
  const hintId = `${inputId}-hint`;

  const confirmed = typed.trim().toLowerCase() === CONFIRM_WORD;
  const canSubmit = confirmed && !saving;

  return (
    <div
      className={`pp-dialog-backdrop${elevated ? ' ctd-backdrop--elevated' : ''}`}
      onClick={onClose}
    >
      <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pp-dialog__header">
          <h2 className="pp-dialog__title">{title}</h2>
          <button className="dp-drawer__close" onClick={onClose}>&#x2715;</button>
        </div>

        <div className="pp-dialog__body">
          {children}

          <div className="dp-drawer__field" style={{ marginTop: 'var(--space-4)' }}>
            <label className="dp-drawer__label" htmlFor={inputId}>
              Type <strong>{CONFIRM_WORD}</strong> to continue
            </label>
            <input
              id={inputId}
              data-help-id={helpIdInput}
              type="text"
              className="dp-drawer__input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              /* Enter submits only once the word matches, so the key that
                 normally means "yes" can't skip the gate. */
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) { e.preventDefault(); onConfirm(); }
              }}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
              autoFocus
              disabled={saving}
              aria-describedby={hintId}
            />
            <p
              id={hintId}
              style={{ margin: 'var(--space-1) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
            >
              {confirmLabel} stays disabled until the word matches.
            </p>
          </div>
        </div>

        <div className="pp-dialog__footer">
          <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="dp-btn dp-btn--danger"
            data-help-id={helpIdConfirm}
            onClick={onConfirm}
            disabled={!canSubmit}
          >
            {saving ? savingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmTypedDialog;
